import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'components/modal';
import { isMobile } from 'react-device-detect';

import {
  AccountAssignRow,
  AddUsersWrapper,
  AnimatedSection,
  AccountAssignCard,
  AccountAssignCardWrapper,
  AccountAssignDescription,
  AccountAssignIcon,
  AssignUsersHeader,
  AccountAssignTitle,
  FooterActions,
  FooterButton,
  FooterToggle,
  HeaderContent,
  AnimatedToggleWrapper,
  ModalContentWrapper,
  SelectedSummary,
  SubTitle,
  UserFilterWrapper,
  UserTeamToggle,
  UserTeamToggleOption,
} from './styles';
import { ModalContainer, ModalSection } from 'components/modal/styles';
import Button from 'components/button';
import {
  IAssignTeamsParams,
  IAssignUsersParams,
  assignTeams,
  assignUsers,
  createPlaylistRule,
  getPlaylistUsersList,
  updatePlaylistRule,
} from 'services/playlists';
import { IPlaylist, IPlaylistUserListItemResponse } from 'contexts/playlists/types';
import Notification from 'components/notification';
import Toggle from 'components/switch';
import Tooltip from 'components/tooltip';
import { Spinner } from 'components/loadings';
import Icon from 'components/icons';
import UserSuggestionItem from 'pages/Teams/components/InviteToTeam/UserSuggestionItem';
import { SessionContext } from 'contexts/session';
import SearchFilter from './SearchFilter';
import {
  ACCOUNT_PLAYLIST_MESSAGES,
  ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS,
  ACCOUNT_PLAYLIST_TOOLTIPS,
  ASSIGN_PLAYLIST_LABEL,
  PLAYLIST_RULE_SCOPE_ACCOUNT,
} from 'constants/playlists';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getInvitations } from 'services/users';
import { UserInvites } from 'contexts/users/types';
import { Team } from 'contexts/teams/types';
import { getTeams } from 'services/teams';
import TeamSuggestionItem from './TeamSuggestionItem';
import { pluralize } from 'utils/string';

interface IAddUsersPlaylist {
  isVisible?: boolean;
  playlist: IPlaylist;
  playlistUsers?: IPlaylistUserListItemResponse[];
  onCancel: () => void;
  onConfirm?: () => void;
}

const AssignUsers: React.FC<IAddUsersPlaylist> = ({
  isVisible,
  playlist,
  playlistUsers,
  onCancel,
  onConfirm,
}) => {
  const title = playlist?.name ? `Assign ${playlist.name} Playlist` : 'Assign playlist';
  const ctaLabel = ASSIGN_PLAYLIST_LABEL;
  const [usersQuery, setUsersQuery] = useState<string>('');
  const [teamsQuery, setTeamsQuery] = useState<string>('');
  const [usersIds, setUsersIds] = useState<string[]>([]);
  const [teamsIds, setTeamsIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'teams'>('users');
  const [pendingRequests, setPendingRequests] = useState<number>(0);
  const [listFading, setListFading] = useState<boolean>(false);
  const [allUsersCount, setAllUsersCount] = useState<number | null>(null);
  const { account, isAccountOwner, isTeamManager } = useContext(SessionContext);
  const { dashboardAccountPlaylistAssignment, dashboardTeamsPlaylistAssignment } = useFlags() || {};
  const isAccountPlaylistFeatureEnabled = Boolean(dashboardAccountPlaylistAssignment);
  const isTeamsPlaylistAssignmentEnabled = Boolean(dashboardTeamsPlaylistAssignment);
  const business_account_id = account?.id;

  const [users, setUsers] = useState<UserInvites[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const showTypeToggle = Boolean(
    (isAccountOwner || isTeamManager) && isTeamsPlaylistAssignmentEnabled
  );
  const fetching = pendingRequests > 0;

  const startFetching = () => {
    setPendingRequests((prevState) => prevState + 1);
  };

  const stopFetching = () => {
    setPendingRequests((prevState) => (prevState > 0 ? prevState - 1 : 0));
  };

  const [assignToAccount, setAssignToAccount] = useState<boolean>(
    isAccountPlaylistFeatureEnabled && Boolean(playlist?.is_account_playlist)
  );
  const showAssignToAccount = isAccountPlaylistFeatureEnabled && Boolean(isAccountOwner);
  const [showTeamToggle, setShowTeamToggle] = useState<boolean>(false);
  const accountUsersCount = useMemo(() => {
    if (typeof allUsersCount === 'number') return allUsersCount;
    const rawCount = account?.users_count;
    if (typeof rawCount === 'number') return rawCount;
    if (Number.isFinite(Number(rawCount))) return Number(rawCount);
    return null;
  }, [account?.users_count, allUsersCount]);
  const assignToAccountLabel = useMemo(() => {
    if (typeof accountUsersCount === 'number') {
      return `Assign to entire account <b>${pluralize(accountUsersCount, 'user')}</b>`;
    }
    return 'Assign to entire account';
  }, [accountUsersCount]);

  const accountTooltip = assignToAccount ? '' : ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.assignInfo;

  const assignedUserTooltip = useMemo(
    () => (
      <span
        dangerouslySetInnerHTML={{
          __html: ACCOUNT_PLAYLIST_TOOLTIPS.removeUserAssignModal,
        }}
      />
    ),
    []
  );

  const getUserId = (user: UserInvites) => user?.uaa_data?.id ?? user?.uaa_id ?? user?.id;
  const getTeamId = (team: Team) => (team.id ? String(team.id) : team.slug || team.name);
  const normalizeId = (id?: string | number | null) => (id ? String(id) : null);
  const assignedUserIdsRef = useRef<Set<string> | null>(null);
  const assignedUserIdsPlaylistRef = useRef<string | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [extraHeaderHeight, setExtraHeaderHeight] = useState(0);
  const assignedTeamIds = useMemo(() => {
    const ids = new Set<string>();
    playlist?.assigned_team_ids?.forEach((id) => {
      const normalized = normalizeId(id);
      if (normalized) ids.add(normalized);
    });
    playlist?.assigned_teams?.forEach((team) => {
      const teamId = getTeamId(team as Team);
      if (teamId) ids.add(teamId);
    });
    return ids;
  }, [playlist?.assigned_team_ids, playlist?.assigned_teams]);

  const buildAssignedUserIds = async () => {
    const ids = new Set<string>();
    playlistUsers?.forEach((user) => {
      const id = normalizeId(user?.user?.id);
      if (id) ids.add(id);
    });
    playlist?.assigned_user_ids?.forEach((id) => {
      const normalized = normalizeId(id);
      if (normalized) ids.add(normalized);
    });

    const playlistId = playlist?.id ?? null;
    if (!playlistId || !business_account_id) {
      return ids;
    }

    if (assignedUserIdsPlaylistRef.current !== playlistId) {
      assignedUserIdsPlaylistRef.current = playlistId;
      assignedUserIdsRef.current = null;
    }

    if (assignedUserIdsRef.current) {
      ids.forEach((id) => assignedUserIdsRef.current?.add(id));
      return assignedUserIdsRef.current;
    }

    try {
      const pageSize = 500;
      const playlistIds = new Set<string>();
      let currentPage = 1;
      let totalUsers = 0;

      do {
        const response = await getPlaylistUsersList({
          account_id: business_account_id,
          playlist_id: playlistId,
          page: currentPage,
          page_size: pageSize,
        });
        const results = response?.data?.results || [];
        totalUsers = response?.data?.count ?? results.length;
        results.forEach((user) => {
          const id = normalizeId(user?.user?.id);
          if (id) playlistIds.add(id);
        });
        if (results.length < pageSize) {
          break;
        }
        currentPage += 1;
      } while ((currentPage - 1) * pageSize < totalUsers);

      ids.forEach((id) => playlistIds.add(id));
      assignedUserIdsRef.current = playlistIds;
      return playlistIds;
    } catch (error) {
      return ids;
    }
  };

  const getUsers = async (query: string) => {
    startFetching();
    try {
      const assignedUserIdsPromise = buildAssignedUserIds();
      const response = await getInvitations({
        active: 'true',
        license: null,
        teams: null,
        roles: null,
        page: 1,
        page_size: 9999,
        search: query || '',
      });
      let filteredUsers: UserInvites[] = [];

      if (response.status === 200) {
        if (!query) {
          setAllUsersCount(response.data.count);
        }
        const assignedUserIds = await assignedUserIdsPromise;
        filteredUsers = response.data.results.filter((user) => {
          const userId = normalizeId(getUserId(user));
          if (!userId) return true;
          return !assignedUserIds.has(userId);
        });
        setUsers(filteredUsers);
      }
    } catch (error) {
      Notification({ text: 'There has been a problem', type: 'error' });
    } finally {
      stopFetching();
    }
  };

  const getTeamsList = async (playlistId?: string) => {
    if (!playlistId) return;
    startFetching();
    try {
      const response = await getTeams({ exclude_playlist_id: playlistId });
      if (response.status === 200) {
        setTeams(response.data);
      }
    } catch (error) {
      const details = error?.response?.data?.details;
      Notification({ text: details || 'There has been a problem', type: 'error' });
    } finally {
      stopFetching();
    }
  };

  useEffect(() => {
    if (activeTab !== 'users') return;
    getUsers(usersQuery);
  }, [
    activeTab,
    usersQuery,
    playlist?.id,
    playlistUsers,
    playlist?.assigned_user_ids,
    business_account_id,
  ]);

  useEffect(() => {
    setListFading(true);
    const timeout = window.setTimeout(() => {
      setListFading(false);
    }, 180);

    return () => window.clearTimeout(timeout);
  }, [activeTab, teamsQuery, usersQuery]);

  useEffect(() => {
    if (!showTypeToggle || !isVisible) return;
    getTeamsList(playlist?.id);
  }, [isVisible, playlist?.id, showTypeToggle]);

  useEffect(() => {
    if (!showTypeToggle) {
      setShowTeamToggle(false);
      return;
    }

    if (assignToAccount) {
      setShowTeamToggle(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowTeamToggle(true);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [assignToAccount, showTypeToggle]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const lineHeight = 32;
    const lines = Math.ceil(el.clientHeight / lineHeight);
    const extra = Math.max(0, lines - 1) * lineHeight;

    setExtraHeaderHeight(extra);
  }, [title]);

  useEffect(() => {
    if (!showTypeToggle && activeTab !== 'users') {
      setActiveTab('users');
    }
  }, [activeTab, showTypeToggle]);

  useEffect(() => {
    if (isTeamsPlaylistAssignmentEnabled) return;
    setTeamsIds([]);
    setActiveTab('users');
  }, [isTeamsPlaylistAssignmentEnabled]);

  useEffect(() => {
    if (activeTab === 'users') {
      setUsersQuery('');
    } else {
      setTeamsQuery('');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isVisible) return;
    if (!isAccountPlaylistFeatureEnabled) {
      setAssignToAccount(false);
      return;
    }
    setAssignToAccount(Boolean(playlist?.is_account_playlist));
  }, [isAccountPlaylistFeatureEnabled, isVisible, playlist?.id, playlist?.is_account_playlist]);

  useEffect(() => {
    if (!assignToAccount) {
      setUsersIds([]);
      setTeamsIds([]);
    }
  }, [assignToAccount]);

  useEffect(() => {
    if (assignedTeamIds.size === 0) return;
    setTeamsIds((prevState) => prevState.filter((id) => !assignedTeamIds.has(id)));
  }, [assignedTeamIds]);

  const onSelectUser = (payload: {
    id?: string | number;
    uaa_id?: string | number;
    uaa_data?: { id?: string | number };
  }) => {
    if (assignToAccount) return;
    const userId = normalizeId(payload?.uaa_data?.id ?? payload?.uaa_id ?? payload?.id);
    if (!userId) return;
    if (usersIds.includes(userId)) {
      setUsersIds((prevState) => prevState.filter((id) => id !== userId));
    } else {
      setUsersIds((prevState) => [...prevState, userId]);
    }
  };

  const onSelectTeam = (team: Team) => {
    if (assignToAccount) return;
    const teamId = normalizeId(getTeamId(team));
    if (!teamId) return;
    if (teamsIds.includes(teamId)) {
      setTeamsIds((prevState) => prevState.filter((id) => id !== teamId));
    } else {
      setTeamsIds((prevState) => [...prevState, teamId]);
    }
  };

  const availableTeams = useMemo(
    () =>
      isTeamsPlaylistAssignmentEnabled
        ? teams.filter((team) => !assignedTeamIds.has(getTeamId(team)))
        : [],
    [assignedTeamIds, isTeamsPlaylistAssignmentEnabled, teams]
  );

  const filteredTeams = useMemo(() => {
    if (!teamsQuery.trim()) return availableTeams;
    const normalizedQuery = teamsQuery.trim().toLowerCase();
    return availableTeams.filter((team) => team.name?.toLowerCase().includes(normalizedQuery));
  }, [availableTeams, teamsQuery]);

  useEffect(() => {
    if (!assignToAccount) return;
    const nextIds = users.map(getUserId).filter(Boolean) as string[];
    setUsersIds((prevState) => {
      if (prevState.length === nextIds.length && nextIds.every((id) => prevState.includes(id))) {
        return prevState;
      }
      return nextIds;
    });
    const nextTeamIds = availableTeams.map(getTeamId).filter(Boolean) as string[];
    setTeamsIds((prevState) => {
      if (
        prevState.length === nextTeamIds.length &&
        nextTeamIds.every((id) => prevState.includes(id))
      ) {
        return prevState;
      }
      return nextTeamIds;
    });
  }, [assignToAccount, availableTeams, users]);

  const selectedSummary = useMemo(() => {
    const usersCount = assignToAccount ? allUsersCount ?? usersIds.length : usersIds.length;
    const teamsCount = isTeamsPlaylistAssignmentEnabled && !assignToAccount ? teamsIds.length : 0;
    const usersLabel = usersCount === 1 ? 'user' : 'users';
    const teamsLabel = teamsCount === 1 ? 'team' : 'teams';

    if (usersCount > 0 && teamsCount > 0) {
      return `Selected: ${usersCount} ${usersLabel} and ${teamsCount} ${teamsLabel}`;
    }

    if (usersCount > 0) {
      return `Selected: ${usersCount} ${usersLabel}`;
    }

    if (teamsCount > 0) {
      return `Selected: ${teamsCount} ${teamsLabel}`;
    }

    return '';
  }, [assignToAccount, allUsersCount, teamsIds.length, usersIds.length]);
  const showSelectedSummary = Boolean(selectedSummary) && !assignToAccount;

  const handleConfirm = async () => {
    const initialAccountAssignment =
      isAccountPlaylistFeatureEnabled && Boolean(playlist?.is_account_playlist);
    const accountAssignmentChanged =
      isAccountPlaylistFeatureEnabled && assignToAccount !== initialAccountAssignment;
    const shouldAssignUsers = !assignToAccount && usersIds.length > 0;
    const shouldAssignTeams =
      isTeamsPlaylistAssignmentEnabled && !assignToAccount && teamsIds.length > 0;
    const buildManualAssignmentMessage = (action: 'assigned' | 'unassigned') => {
      const usersCount = usersIds.length;
      const teamsCount = isTeamsPlaylistAssignmentEnabled ? teamsIds.length : 0;
      if (usersCount > 0 && teamsCount > 0) {
        return `Playlist ${action} to ${pluralize(usersCount, 'user')} and ${pluralize(
          teamsCount,
          'team'
        )}`;
      }
      if (usersCount > 0) {
        return `Playlist ${action} to ${pluralize(usersCount, 'user')}`;
      }
      if (teamsCount > 0) {
        return `Playlist ${action} to ${pluralize(teamsCount, 'team')}`;
      }
      return '';
    };

    if (!shouldAssignUsers && !shouldAssignTeams && !accountAssignmentChanged) return;

    startFetching();
    try {
      const accountAssignmentPromise = accountAssignmentChanged
        ? assignToAccount
          ? createPlaylistRule({
              playlist_id: playlist.id,
              scope: PLAYLIST_RULE_SCOPE_ACCOUNT,
            })
          : updatePlaylistRule({
              playlist_id: playlist.id,
              scope: PLAYLIST_RULE_SCOPE_ACCOUNT,
            })
        : Promise.resolve(null);

      const assignUsersPromise = shouldAssignUsers
        ? assignUsers({
            id: playlist.id,
            user_ids: usersIds,
            business_account_id: business_account_id,
          } as IAssignUsersParams)
        : Promise.resolve(null);

      const assignTeamsPromise = shouldAssignTeams
        ? assignTeams({
            id: playlist.id,
            team_ids: teamsIds,
            business_account_id: business_account_id,
          } as IAssignTeamsParams)
        : Promise.resolve(null);

      const [assignUsersResponse, assignTeamsResponse, accountResponse] = await Promise.all([
        assignUsersPromise,
        assignTeamsPromise,
        accountAssignmentPromise,
      ]);

      const assignUsersOk =
        !assignUsersResponse ||
        (assignUsersResponse?.status >= 200 && assignUsersResponse?.status < 300);
      const assignTeamsOk =
        !assignTeamsResponse ||
        (assignTeamsResponse?.status >= 200 && assignTeamsResponse?.status < 300);
      const accountOk =
        !accountResponse || (accountResponse?.status >= 200 && accountResponse?.status < 300);

      if (assignUsersOk && assignTeamsOk && accountOk) {
        const successMessage =
          shouldAssignUsers || shouldAssignTeams
            ? buildManualAssignmentMessage('assigned')
            : assignToAccount
            ? ACCOUNT_PLAYLIST_MESSAGES.assigned
            : ACCOUNT_PLAYLIST_MESSAGES.unassigned;
        Notification({
          text: successMessage,
          type: 'success',
          singleLine: true,
        });
        if (onConfirm) onConfirm();
        else onCancel();
      } else {
        Notification({ text: 'There has been a problem assigning playlist', type: 'error' });
      }
    } catch (error) {
      if (error?.response?.data?.details) {
        Notification({ text: error.response.data.details, type: 'error' });
      } else {
        Notification({ text: 'There has been a problem assigning playlist', type: 'error' });
      }
    } finally {
      stopFetching();
    }
  };

  const isTeamsView = activeTab === 'teams';
  const subtitle = isTeamsView
    ? 'Assign to current and future members of teams'
    : 'Assign to individuals';
  const teamToggleOptions = [
    { value: 'users' as const, label: 'Select users' },
    { value: 'teams' as const, label: 'Select teams' },
  ];

  return (
    <Modal
      centered
      visible={isVisible}
      fullscreen={isMobile}
      onCancel={onCancel}
      width={isMobile ? '100%' : '595px'}
      footer={null}
      maskClosable={false}
      closeIconColor="#FFF"
      className={'assign-users'}
    >
      <ModalContainer lock={fetching}>
        <AssignUsersHeader assignedToAccount={assignToAccount} extraHeight={extraHeaderHeight}>
          <HeaderContent>
            <h2 ref={titleRef}>{title}</h2>
            {showTypeToggle && showTeamToggle && !assignToAccount && (
              <AnimatedToggleWrapper>
                <UserTeamToggle>
                  {teamToggleOptions.map((option) => (
                    <UserTeamToggleOption
                      key={option.value}
                      type="button"
                      $active={activeTab === option.value}
                      onClick={() => setActiveTab(option.value)}
                    >
                      {option.label}
                    </UserTeamToggleOption>
                  ))}
                </UserTeamToggle>
              </AnimatedToggleWrapper>
            )}
          </HeaderContent>
        </AssignUsersHeader>
        <AnimatedSection key={activeTab}>
          {assignToAccount ? (
            <ModalContentWrapper>
              <AccountAssignCardWrapper>
                <AccountAssignCard>
                  <AccountAssignIcon>
                    <Icon icon="AccountAvatar" size="extraLarge" />
                  </AccountAssignIcon>
                  <div>
                    <AccountAssignTitle>Assign to entire account</AccountAssignTitle>
                    <AccountAssignDescription>
                      The playlist will be assigned automatically to all current users and new users
                      who join the account.
                    </AccountAssignDescription>
                  </div>
                </AccountAssignCard>
              </AccountAssignCardWrapper>
            </ModalContentWrapper>
          ) : (
            <>
              <ModalContentWrapper>
                <ModalSection>
                  <SubTitle>{subtitle}</SubTitle>
                </ModalSection>
              </ModalContentWrapper>
              <ModalContentWrapper>
                <AddUsersWrapper>
                  <ModalSection>
                    <SearchFilter
                      key={activeTab}
                      onChangeQuery={(value) =>
                        isTeamsView ? setTeamsQuery(value) : setUsersQuery(value)
                      }
                      placeholder={isTeamsView ? 'Search teams' : 'Search users'}
                      ariaLabel={isTeamsView ? 'search teams' : 'search users'}
                    />
                    <UserFilterWrapper $fading={listFading}>
                      {isTeamsView
                        ? filteredTeams.map((team) => {
                            const teamId = getTeamId(team);
                            return (
                              <TeamSuggestionItem
                                key={teamId}
                                team={team}
                                checked={Boolean(teamId && teamsIds.includes(teamId))}
                                onCheck={onSelectTeam}
                                disabled={assignToAccount}
                              />
                            );
                          })
                        : users.map((user) => {
                            const userId = normalizeId(getUserId(user));
                            return (
                              <UserSuggestionItem
                                key={userId ?? user.uaa_data?.id ?? user.uaa_id ?? user.id}
                                {...user}
                                checked={Boolean(userId && usersIds.includes(userId))}
                                onCheck={onSelectUser}
                                disabled={assignToAccount}
                                checkboxTooltip={assignToAccount ? assignedUserTooltip : undefined}
                                checkboxTooltipPlacement="right"
                              />
                            );
                          })}
                    </UserFilterWrapper>
                  </ModalSection>
                </AddUsersWrapper>
              </ModalContentWrapper>
            </>
          )}
        </AnimatedSection>
        <FooterActions>
          {showAssignToAccount ? (
            <FooterToggle>
              <AccountAssignRow>
                <Tooltip
                  destroyTooltipOnHide
                  placement="top"
                  overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
                  getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                  zIndex={4000}
                  title={accountTooltip || null}
                >
                  <span>
                    <Toggle
                      variant="checkbox"
                      checked={assignToAccount}
                      onChange={(value) => setAssignToAccount(value)}
                      label={assignToAccountLabel}
                      className="assign-account-toggle"
                    />
                  </span>
                </Tooltip>
              </AccountAssignRow>
            </FooterToggle>
          ) : null}
          <SelectedSummary $hidden={!showSelectedSummary} aria-hidden={!showSelectedSummary}>
            {selectedSummary}
          </SelectedSummary>
          <FooterButton>
            <Button $primary onClick={handleConfirm}>
              {ctaLabel}
            </Button>
          </FooterButton>
        </FooterActions>
        <Spinner active={fetching} top={isMobile ? '250px' : '300px'} />
      </ModalContainer>
    </Modal>
  );
};

export default AssignUsers;
